/**
 * Universal price parsing and formatting utilities for paywall apps.
 *
 * Handles localized price strings from native IAP systems with various formats:
 * - Currency prefix: "$60.00", "€45.00", "£30.00"
 * - Currency suffix: "60.00 S₣", "45,00 €"
 * - Different decimal separators: "60.00" vs "60,00"
 */

/**
 * Removes trailing zeros from a price string while preserving currency symbols.
 *
 * @example
 * removeTrailingZeros("$10.50") // "$10.5"
 * removeTrailingZeros("$10.00") // "$10"
 * removeTrailingZeros("50.00 S₣") // "50 S₣"
 * removeTrailingZeros("€45,00") // "€45"
 */
export function removeTrailingZeros(price: string): string {
  // Handle both dot and comma as decimal separators
  // Skip separators followed by exactly 3 digits (thousands separators)
  const notThousands = '(?!\\d{3}(?:\\s|[^\\d]|$))'
  return price
    .replace(new RegExp(`([.,])${notThousands}0+(?=\\s|[^\\d]|$)`, 'g'), '')
    .replace(
      new RegExp(`([.,])${notThousands}(\\d*?)0+(?=\\s|[^\\d]|$)`, 'g'),
      '$1$2',
    )
    .replace(new RegExp(`([.,])${notThousands}(?=\\s|[^\\d]|$)`, 'g'), '')
}

interface PriceComponents {
  /** The numeric value extracted from the price string */
  value: number
  /** The currency symbol (e.g., "$", "€", "S₣") */
  currency: string
  /** Whether the currency symbol appears before the number */
  isCurrencyPrefix: boolean
  /** The decimal separator used ("." or ",") */
  decimalSeparator: string
}

/**
 * Parses a localized price string into its components.
 *
 * @example
 * parsePriceComponents("$60.00") // { value: 60, currency: "$", isCurrencyPrefix: true, decimalSeparator: "." }
 * parsePriceComponents("45,00 €") // { value: 45, currency: "€", isCurrencyPrefix: false, decimalSeparator: "," }
 */
export function parsePriceComponents(priceLocalized: string): PriceComponents {
  // Extract full numeric part including thousands separators (e.g., "1,200.50")
  const numericMatch = priceLocalized.match(/[\d]+(?:[.,]\d+)*/g)
  const numericStr = numericMatch?.[0] ?? '0'

  const lastDotIndex = numericStr.lastIndexOf('.')
  const lastCommaIndex = numericStr.lastIndexOf(',')

  let decimalSeparator: string
  let value: number

  if (lastDotIndex > -1 && lastCommaIndex > -1) {
    // Both separators present — the last one is the decimal separator
    if (lastDotIndex > lastCommaIndex) {
      decimalSeparator = '.'
      value = parseFloat(numericStr.replace(/,/g, ''))
    } else {
      decimalSeparator = ','
      value = parseFloat(numericStr.replace(/\./g, '').replace(',', '.'))
    }
  } else if (lastCommaIndex > -1) {
    const digitsAfterComma = numericStr.length - lastCommaIndex - 1
    if (digitsAfterComma === 3) {
      // Comma is a thousands separator (e.g., "12,000")
      decimalSeparator = '.'
      value = parseFloat(numericStr.replace(/,/g, ''))
    } else {
      // Comma is the decimal separator (e.g., "9,99")
      decimalSeparator = ','
      const intPart = numericStr.substring(0, lastCommaIndex).replace(/,/g, '')
      const decPart = numericStr.substring(lastCommaIndex + 1)
      value = parseFloat(`${intPart}.${decPart}`)
    }
  } else if (lastDotIndex > -1) {
    const digitsAfterDot = numericStr.length - lastDotIndex - 1
    if (digitsAfterDot === 3) {
      // Dot is a thousands separator (e.g., "1.200") — locale uses comma as decimal
      decimalSeparator = ','
      value = parseFloat(numericStr.replace(/\./g, ''))
    } else {
      // Dot is the decimal separator (e.g., "9.99")
      decimalSeparator = '.'
      value = parseFloat(numericStr)
    }
  } else {
    // No separator (e.g., "60")
    decimalSeparator = '.'
    value = parseFloat(numericStr)
  }

  // Extract currency (everything that's not a digit, decimal separator, or whitespace)
  const currency = priceLocalized.replace(/[\d.,\s]/g, '').trim()

  // Determine if currency is prefix or suffix
  const trimmed = priceLocalized.trim()
  const isCurrencyPrefix = !trimmed.match(/^[\d]/)

  return {
    value,
    currency,
    isCurrencyPrefix,
    decimalSeparator,
  }
}

interface FormatPriceOptions {
  /** Number of decimal places (default: 2) */
  decimals?: number
  /** Remove trailing zeros (default: true) */
  removeTrailingZeros?: boolean
}

/**
 * Formats a numeric value using the format from a localized price string.
 * Preserves currency symbol position and decimal separator.
 *
 * @example
 * formatPriceWithLocale(5, "$60.00") // "$5"
 * formatPriceWithLocale(5.5, "$60.00") // "$5.5"
 * formatPriceWithLocale(5, "60,00 €") // "5 €"
 */
export function formatPriceWithLocale(
  value: number,
  priceLocalized: string,
  options: FormatPriceOptions = {},
): string {
  const { decimals = 2, removeTrailingZeros: shouldRemoveZeros = true } =
    options

  const { currency, isCurrencyPrefix, decimalSeparator } =
    parsePriceComponents(priceLocalized)

  // Format the number with the appropriate decimal separator
  let formattedValue = value.toFixed(decimals)
  if (decimalSeparator === ',') {
    formattedValue = formattedValue.replace('.', ',')
  }

  // Construct the price string
  let result: string
  if (isCurrencyPrefix) {
    result = `${currency}${formattedValue}`
  } else {
    result = `${formattedValue} ${currency}`
  }

  // Remove trailing zeros if requested
  if (shouldRemoveZeros) {
    result = removeTrailingZeros(result)
  }

  return result
}

/**
 * Calculates the monthly price from an annual subscription price.
 * Preserves the original price format (currency position, decimal separator).
 *
 * @example
 * calculateMonthlyPrice({ price_localized: "$60.00", price_value: 60 })
 * // "$5"
 *
 * calculateMonthlyPrice({ price_localized: "120,00 €", price_value: 120 })
 * // "10 €"
 */
export function calculateMonthlyPrice(product: {
  price_localized: string
  price_value: number
}): string {
  const monthlyValue = product.price_value / 12
  return formatPriceWithLocale(monthlyValue, product.price_localized)
}

/**
 * Replaces the numeric value in a localized price string with a new value.
 * Useful for displaying calculated prices (e.g., per-month) while preserving
 * the original currency format.
 *
 * @example
 * replacePrice("$60.00", 5) // "$5"
 * replacePrice("60,00 €", 5) // "5 €"
 * replacePrice("S₣ 120.00", 10) // "S₣ 10"
 */
export function replacePrice(
  priceLocalized: string,
  newValue: number,
  options: FormatPriceOptions = {},
): string {
  return formatPriceWithLocale(newValue, priceLocalized, options)
}
